import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { EVENT } from '../../../util/events'
import { keyed } from 'lit/directives/keyed.js'

@customElement('spouse-card')
export class SpouseCard extends LitElement {
  static styles = css`
    .required-star {
      color: var(--lh-color-danger-600, #cc393b);
    }
  `
  @property({ type: Boolean })
  dentalSelected = false

  @property({ type: Boolean })
  visionSelected = false

  @property({ type: String })
  spouseDob: string = ''

  // Persisted checkbox state from parent context
  @property({ type: Boolean })
  dentalChecked?: boolean

  @property({ type: Boolean })
  visionChecked?: boolean

  @state()
  private spouseDentalChecked = false

  @state()
  private spouseVisionChecked = false

  @state()
  private isOverAge = false

  get formattedSpouseDob(): string {
    return this.formatDateToMMDDYYYY(this.spouseDob)
  }

  private sanitizeDobValue(date: string): string {
    if (!date) return ''
    return date.replace(/undefined/gi, '').replace(/\/+/g, '/')
  }

  private formatDateToMMDDYYYY(date: string): string {
    if (!date) return ''
    const sanitized = this.sanitizeDobValue(date)
    if (sanitized.includes('/')) return sanitized
    const parts = sanitized.split('-')
    if (parts.length === 3) {
      return this.sanitizeDobValue(`${parts[1]}/${parts[2]}/${parts[0]}`)
    }
    return sanitized
  }

  firstUpdated() {
    // Initialize from persisted state when available; otherwise default to plan selections
    this.spouseDentalChecked = this.dentalChecked ?? this.dentalSelected
    this.spouseVisionChecked = this.visionChecked ?? this.visionSelected
    this.updateAgeFlag(this.spouseDob)
    this.emitDataChange()
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties)

    if (changedProperties.has('spouseDob') && this.spouseDob) {
      const storedDob = this.spouseDob
      setTimeout(() => {
        if (!this.isConnected) return
        const dobInput = this.shadowRoot?.querySelector(
          'lh-input[name="dob"]'
        ) as { change?: (value: string) => void } | null
        const iso = storedDob.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        dobInput?.change?.(iso ? `${iso[2]}/${iso[3]}/${iso[1]}` : storedDob)
      }, 0)
    }

    if (changedProperties.has('dentalSelected')) {
      this.spouseDentalChecked = this.dentalSelected
      this.emitDataChange()
      // Force re-render to update checkbox
      this.requestUpdate()
    }
    if (changedProperties.has('visionSelected')) {
      this.spouseVisionChecked = this.visionSelected
      this.emitDataChange()
      // Force re-render to update checkbox
      this.requestUpdate()
    }
    if (changedProperties.has('dentalChecked')) {
      this.spouseDentalChecked =
        typeof this.dentalChecked === 'boolean'
          ? this.dentalChecked
          : this.spouseDentalChecked
      this.requestUpdate()
    }
    if (changedProperties.has('visionChecked')) {
      this.spouseVisionChecked =
        typeof this.visionChecked === 'boolean'
          ? this.visionChecked
          : this.spouseVisionChecked
      this.requestUpdate()
    }
    if (changedProperties.has('spouseDob')) {
      this.updateAgeFlag(this.spouseDob)
      // reflect any changes to parent consumers
      this.emitDataChange()
    }
  }

  private handleCheckboxChange(event: CustomEvent) {
    const path = event.composedPath()
    const checkbox = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-checkbox'
    ) as HTMLElement

    if (checkbox) {
      const name = checkbox.getAttribute('name')
      const checked = (event as any).detail?.checked

      if (name === 'spouse-dental') {
        this.spouseDentalChecked = checked
        this.emitDataChange()
      } else if (name === 'spouse-vision') {
        this.spouseVisionChecked = checked
        this.emitDataChange()
      }
    }
  }

  private handleDobChange(event: CustomEvent) {
    const path = event.composedPath()
    const input = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-input'
    ) as HTMLElement

    if (input && input.getAttribute('name') === 'dob') {
      const newDob = this.sanitizeDobValue((event as any).detail?.value || '')
      this.spouseDob = newDob
      this.calculateAndSetOverAge(newDob)
      this.emitDataChange()
    }
  }

  private calculateAndSetOverAge(dobStr: string) {
    if (!dobStr) {
      this.isOverAge = false
      return
    }
    const today = new Date()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const yyyy = String(today.getFullYear())
    const todayStr = `${mm}/${dd}/${yyyy}`
    const age = this.calcAgeOnDate(dobStr, todayStr)
    // Check if date is in the future or age is over 120
    const dob = this.parseMMDDYYYY(dobStr)
    const dobIsInFuture = dob && dob > today
    this.isOverAge = dobIsInFuture || (age ?? 0) > 120
  }

  private emitDataChange() {
    const dobInput = this.shadowRoot?.querySelector('[name="dob"]') as any

    this.dispatchEvent(
      new CustomEvent('spouse-data-change', {
        detail: {
          dob: dobInput?.value || this.spouseDob || '',
          dentalChecked: this.spouseDentalChecked,
          visionChecked: this.spouseVisionChecked,
          overAge: this.isOverAge,
        },
        bubbles: true,
        composed: true,
      })
    )
  }

  private parseMMDDYYYY(dateStr: string): Date | null {
    if (!dateStr) return null
    const normalized = dateStr.includes('/')
      ? dateStr
      : this.formatDateToMMDDYYYY(dateStr)
    const parts = normalized.split('/')
    if (parts.length !== 3) return null
    const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10))
    if (!mm || !dd || !yyyy) return null
    return new Date(yyyy, mm - 1, dd)
  }

  private calcAgeOnDate(dobStr: string, onDateStr: string): number | null {
    const dob = this.parseMMDDYYYY(dobStr)
    const onDate = this.parseMMDDYYYY(onDateStr)
    if (!dob || !onDate) return null
    let age = onDate.getFullYear() - dob.getFullYear()
    const m = onDate.getMonth() - dob.getMonth()
    if (m < 0 || (m === 0 && onDate.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  private updateAgeFlag(dobStr: string) {
    if (!dobStr) {
      this.isOverAge = false
      return
    }
    const today = new Date()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const yyyy = String(today.getFullYear())
    const todayStr = `${mm}/${dd}/${yyyy}`
    const age = this.calcAgeOnDate(dobStr, todayStr)
    // Check if date is in the future or age is over 120
    const dob = this.parseMMDDYYYY(dobStr)
    const dobIsInFuture = dob && dob > today
    this.isOverAge = dobIsInFuture || (age ?? 0) > 120
    this.requestUpdate()
  }

  private handleChange(event: CustomEvent) {
    const path = event.composedPath()
    const input = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-input'
    ) as HTMLElement
    const checkbox = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-checkbox'
    ) as HTMLElement

    if (input) {
      this.handleDobChange(event)
    } else if (checkbox) {
      this.handleCheckboxChange(event)
    }
  }

  private getDobErrorMessage(): string {
    if (!this.isOverAge) return ''
    const dob = this.parseMMDDYYYY(this.spouseDob)
    const today = new Date()
    if (dob && dob > today) {
      return 'Date of birth cannot be in the future.'
    }
    return 'Must be a valid date of birth (age up to 120).'
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent('spouse-close', {
        bubbles: true,
        composed: true,
      })
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener(EVENT.LH_CLOSE, this.handleClose as EventListener)
    this.addEventListener(EVENT.LH_CHANGE, this.handleChange as EventListener)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(EVENT.LH_CLOSE, this.handleClose as EventListener)
    this.removeEventListener(
      EVENT.LH_CHANGE,
      this.handleChange as EventListener
    )
  }

  render() {
    return html`
      <lh-grid gap="md">
        <lh-grid-item>
          <lh-container
            margin-top="sm"
            spacing="none"
            gutters="none"
            context-id="spouse-card"
          >
            <lh-dismissible-card>
              <lh-typography size="text-">
                <strong>Spouse / domestic partner</strong>
              </lh-typography>
              <lh-grid>
                <lh-grid-item span="6">
                  <lh-input
                    name="dob"
                    type="dob"
                    default-value=${this.formattedSpouseDob}
                    error-message=${this.getDobErrorMessage()}
                    required
                  >
                    Date of birth
                  </lh-input>
                </lh-grid-item>
              </lh-grid>
              <lh-typography size="text-md">
                <strong
                  >Select the coverage for this applicant
                  <span class="required-star">*</span></strong
                >
              </lh-typography>
              ${keyed(
                this.spouseDentalChecked,
                html`<lh-checkbox
                  name="spouse-dental"
                  value="dental"
                  ?disabled=${!this.dentalSelected}
                  ?default-checked=${this.spouseDentalChecked}
                >
                  Dental coverage
                </lh-checkbox>`
              )}
              ${!this.dentalSelected
                ? html`<lh-typography variant="p" size="text-md">
                    <em>
                      Primary subscriber must enroll in dental coverage to add
                      dental for a spouse or dependent.
                    </em>
                  </lh-typography>`
                : ''}
              ${keyed(
                this.spouseVisionChecked,
                html`<lh-checkbox
                  name="spouse-vision"
                  value="vision"
                  ?disabled=${!this.visionSelected}
                  ?default-checked=${this.spouseVisionChecked}
                >
                  Vision coverage
                </lh-checkbox>`
              )}
              ${!this.visionSelected
                ? html`<lh-typography variant="p" size="text-md">
                    <em>
                      Primary subscriber must enroll in vision coverage to add
                      vision for a spouse or dependent.
                    </em>
                  </lh-typography>`
                : ''}
            </lh-dismissible-card>
          </lh-container>
        </lh-grid-item>
      </lh-grid>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'spouse-card': SpouseCard
  }
}
